import { Amount } from '@mews-commander/types';
import { SpacingMediumLeftRight, BreakpointRange, Responsive, OptimusProvider } from '@mews-commander/ui';
import { ExpandableBox } from '@optimus-web/helpers';
import { ClickableDiv, Container as OptimusContainer, Stack } from '@optimus-web/core';
import { useAppClockService, useFeatureFlag } from '@mews/framework';
import { isAccountIdCompany } from '@mews-commander/legacy-entities/src/selectors/bills';
import { isBillVirtual } from '@mews-commander/legacy-entities/src/ducks';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { FeatureFlag } from '@mews-commander/enums';
import Balance from '../../components/Balance';
import {
    isBillChargeable as isBillChargeableSelector,
    isBillClosable as isBillClosableSelector,
} from '../../selectors';
import { BillClassification } from '../../constants';
import { ProcessPaymentType } from '../../../payment';
import { refetchBill } from '../../ducks/bills';
import { fetchLinkedReservationOwners } from '../../utils/payments';
import BillPreviewButton from './BillPreviewButton';
import { ChargeAndClose } from './ChargeAndClose';
import { CloseBill } from './CloseBill';
import InvoiceDropDown from './InvoiceDropdown';
import { Container, SizedContainerMap } from './style';
import { ViewBillInAccountButton } from './ViewBillInAccountButton';
import { BillProcessPaymentDialogButton } from './BillProcessPaymentDialogButton';

interface PaymentCardProps {
    amount?: Amount;
    billId: string;
    accountId: string;
    billClassification?: BillClassification;
    expanded: boolean;
    onClick(): void;
}

const Wrapper: React.FC<React.PropsWithChildren<{}>> = ({ children }) => <div>{children}</div>;
export const PaymentCard: React.FC<PaymentCardProps> = ({
    amount,
    billId,
    expanded,
    accountId,
    billClassification,
    onClick,
}) => {
    const appClockService = useAppClockService();
    const dispatch = useCommanderThunkDispatch();
    const isCompanyTerminalPaymentsEnabled = useFeatureFlag(FeatureFlag.companyTerminalPaymentsEnabled);

    const isBillChargeable = useCommanderSelector((state) =>
        isBillChargeableSelector(state, { accountId, appClockService, amount })
    );

    const isBillClosable = useCommanderSelector((state) => isBillClosableSelector(state, { billId, amount }));
    const isCompanyBilling = useCommanderSelector((state) => isAccountIdCompany(state, { accountId }));
    const isVirtualBill = useCommanderSelector((state) => isBillVirtual(state, { id: billId }));

    const onProcessPaymentFormSuccess = () => {
        const finalBillId = isVirtualBill ? undefined : billId;
        dispatch(
            refetchBill({
                billId: finalBillId,
                accountId,
                extent: { payments: true, paymentCards: true, serviceOrders: true, orderItems: true, products: true },
            })
        );
        dispatch(fetchLinkedReservationOwners({ billIds: [billId], accountId }));
    };

    const allowedPaymentTypes = isCompanyBilling
        ? [
              ProcessPaymentType.CashPayment,
              ProcessPaymentType.ExternalPayment,
              ProcessPaymentType.GatewayPayment,
              ProcessPaymentType.ExternalCardPayment,
              ...(isCompanyTerminalPaymentsEnabled ? [ProcessPaymentType.MewsTerminalPayment] : []),
              ProcessPaymentType.PaymentRequest,
          ]
        : undefined;

    return (
        <Responsive
            Default={Wrapper}
            Phone={SpacingMediumLeftRight}
            Tablet={SizedContainerMap[BreakpointRange.Tablet.name]}
            Desktop={SizedContainerMap[BreakpointRange.Desktop.name]}
            LargeDesktop={SizedContainerMap[BreakpointRange.LargeDesktop.name]}
        >
            <ClickableDiv onClick={onClick}>
                <Container>
                    <OptimusContainer padding="200" data-test-payment-amount>
                        {amount ? <Balance amount={amount} /> : null}
                    </OptimusContainer>
                </Container>
            </ClickableDiv>
            <ExpandableBox expanded={expanded}>
                <OptimusContainer padding={['200', '0']}>
                    {billClassification && billClassification === BillClassification.Associated ? (
                        <ViewBillInAccountButton billId={billId} accountId={accountId} />
                    ) : (
                        <Stack vertical spacing="200">
                            <OptimusProvider density="spacious">
                                <BillPreviewButton billId={billId} />
                                <Stack spacing="200" vertical>
                                    <BillProcessPaymentDialogButton
                                        accountId={accountId}
                                        allowedPaymentTypes={allowedPaymentTypes}
                                        billId={billId}
                                        onFormSuccess={onProcessPaymentFormSuccess}
                                    />
                                    <InvoiceDropDown amount={amount} billId={billId} />
                                </Stack>
                                {isBillChargeable && !isCompanyBilling ? (
                                    <ChargeAndClose billId={billId} accountId={accountId} />
                                ) : null}
                                {isBillClosable ? <CloseBill billId={billId} /> : null}
                            </OptimusProvider>
                        </Stack>
                    )}
                </OptimusContainer>
            </ExpandableBox>
        </Responsive>
    );
};
